import {
  DocumentAttached,
  EducationalHistory,
  ScholarshipApplicationDetailsProps
} from "../typings";

/**
 * Maps specific fields from parent_application object to the outer application object
 * @param application - The scholarship application object that may contain a parent_application
 * @returns The application object with mapped fields from parent_application
 */
export const mapParentApplicationFields = (
  application: ScholarshipApplicationDetailsProps
): ScholarshipApplicationDetailsProps => {
  if (!application?.parent_application) {
    return application;
  }

  const { parent_application } = application;
  const mappedApplication = { ...application };

  // Map accommodation_details from parent_application
  if (parent_application.accommodation_details) {
    mappedApplication.accommodation_details = parent_application.accommodation_details;
  }

  // Map documents_attached - only add documents that don't exist in the outer object
  if (parent_application.documents_attached && Array.isArray(parent_application.documents_attached)) {
    const existingTitles = new Set(
      (mappedApplication.documents_attached || []).map((doc: DocumentAttached) => doc.title)
    );
    
    const newDocuments = parent_application.documents_attached.filter(
      (doc: DocumentAttached) => !existingTitles.has(doc.title)
    );
    
    mappedApplication.documents_attached = [
      ...(mappedApplication.documents_attached || []),
      ...newDocuments
    ];
  }

  // Map other_scholarships from parent_application
  if (parent_application.other_scholarships) {
    mappedApplication.other_scholarships = parent_application.other_scholarships;
  }

  // Map personal_statements from parent_application
  if (parent_application.personal_statements) {
    mappedApplication.personal_statements = parent_application.personal_statements;
  }

  // Map photo from parent_application only if outer object doesn't have a value
  if (parent_application.photo && !mappedApplication.photo) {
    mappedApplication.photo = parent_application.photo;
  }

  // Map educational_history from parent_application - exclude university entries
  if (parent_application.educational_history && parent_application.educational_history.length > 0) {
    const nonUniversityEducation = parent_application.educational_history.filter(
      (edu: EducationalHistory) => !edu.is_university
    );
    
    if (nonUniversityEducation.length > 0) {
      // Combine existing educational history with non-university entries from parent
      const existingEducation = mappedApplication.educational_history || [];
      mappedApplication.educational_history = [...nonUniversityEducation,...existingEducation ];
    }
  }

  // Map studying_family_members from parent_application
  if (parent_application.studying_family_members.length > 0) {
    mappedApplication.studying_family_members = parent_application.studying_family_members;
  }

  // Map earning_family_members from parent_application
  if (parent_application.earning_family_members.length > 0) {
    mappedApplication.earning_family_members = parent_application.earning_family_members;
  }

  if (parent_application.family_expenses.length > 0) {
    mappedApplication.family_expenses = parent_application.family_expenses;
  }

  if (parent_application.family_liabilities.length > 0) {
    mappedApplication.family_liabilities = parent_application.family_liabilities;
  }

  return mappedApplication;
}; 